import { useEffect, useRef } from 'react';
import type { ConnectionStatus } from '@orborix/voice-client';
import { track, type VoiceSessionMode } from '../../../lib/analytics.js';
import type { ConversationStartContext } from './useStartContext.js';

/** The analytics mode of a conversation, from its frozen start context. */
export function voiceSessionMode(start: ConversationStartContext): VoiceSessionMode {
  if (start.examMode) return 'exam';
  if (start.a0Mission) return 'a0';
  if (start.scenario) return 'scenario';
  if (start.overrideUnitId) return 'program';
  if (start.listenStart) return 'listen';
  if (start.shortStart) return 'orbitas';
  return 'free';
}

interface Opts {
  connectionStatus: ConnectionStatus;
  start: ConversationStartContext;
  /** Learner turns in the thread so far (a resumed session starts with the old ones). */
  userTurns: number;
}

/**
 * `voice_session_started` when the session first goes `open`, `voice_session_ended`
 * when it is back to `idle` (or the screen unmounts mid-session), plus
 * `program_unit_started` for a Program unit. Duration and turn count only — nothing
 * that was said (lib/analytics.ts, no-PII rule). Reconnects (`reconnecting` / `lost`
 * → `open`) belong to the same session.
 */
export function useVoiceSessionAnalytics({ connectionStatus, start, userTurns }: Opts): void {
  const liveRef = useRef<{ at: number; turnsAtStart: number } | null>(null);
  const turnsRef = useRef(userTurns);
  turnsRef.current = userTurns;
  const mode = voiceSessionMode(start);
  const resumed = start.resumeSessionId !== null;
  const unitId = start.overrideUnitId;
  const metaRef = useRef({ mode, resumed });
  metaRef.current = { mode, resumed };

  useEffect(() => {
    if (connectionStatus === 'open' && liveRef.current === null) {
      liveRef.current = { at: Date.now(), turnsAtStart: turnsRef.current };
      track('voice_session_started', { mode, resumed });
      if (unitId) track('program_unit_started', { unit_id: unitId, resumed });
    } else if (connectionStatus === 'idle' && liveRef.current !== null) {
      endSession(liveRef, turnsRef.current, metaRef.current);
    }
  }, [connectionStatus, mode, resumed, unitId]);

  useEffect(
    () => () => {
      if (liveRef.current !== null) endSession(liveRef, turnsRef.current, metaRef.current);
    },
    [],
  );
}

function endSession(
  liveRef: { current: { at: number; turnsAtStart: number } | null },
  turns: number,
  meta: { mode: VoiceSessionMode; resumed: boolean },
): void {
  const live = liveRef.current;
  if (!live) return;
  liveRef.current = null;
  track('voice_session_ended', {
    ...meta,
    duration_s: Math.round((Date.now() - live.at) / 1000),
    turns: Math.max(0, turns - live.turnsAtStart),
  });
}
